import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { inferRouterOutputs } from '@trpc/server';
import { Link } from 'react-router-dom';
import { trpc } from '@/api/trpc';
import type { AppRouter } from '@/types/appRouter';
import { showToast } from '@components/Layout/Toast';
import Button from '@components/UI/Button';
import Card from '@components/UI/Card';
import FormCheckbox from '@components/UI/FormCheckbox';
import FormSelect from '@components/UI/FormSelect';
import SearchSelect from '@/components/UI/SearchSelect';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { useAppQuery } from '@/hooks/useAppQuery';

type RouterOutputs = inferRouterOutputs<AppRouter>;
type AeroProfileStrategy = RouterOutputs['exitOptimization']['fetchExitSelectionProfileOptions']['strategies'][number];
type AeroProfileJobOption = AeroProfileStrategy['jobs'][number];
type AeroExitSelectionBinding = AeroProfileStrategy['bindings'][number];
type AeroExitSelectionMode = AeroExitSelectionBinding['selectionMode'];

type AeroDraft = {
    jobId: string;
    selectionMode: AeroExitSelectionMode;
    requireOptionBuyingStrategyMatch: boolean;
};

const SELECTION_MODE_OPTIONS: { label: string; value: AeroExitSelectionMode }[] = [
    { label: 'Entity with overall fallback', value: 'entity_with_overall_fallback' },
    { label: 'Overall winner', value: 'overall' },
    { label: 'Entity only', value: 'entity' }
];

const formatJobName = (job: Pick<AeroProfileJobOption, 'id' | 'name'>) => job.name?.trim() || `AERO Job #${job.id}`;

const formatUnixTimestamp = (timestamp: number | null) => {
    if (!timestamp) return 'None';
    return new Date(timestamp * 1000).toLocaleString();
};

const modeNeedsEntitySnapshots = (mode: AeroExitSelectionMode) => mode === 'entity';

const isModeSupportedByJob = (mode: AeroExitSelectionMode, job: AeroProfileJobOption | null | undefined) => {
    if (!job) return false;
    if (modeNeedsEntitySnapshots(mode)) return job.activeEntitySnapshotCount > 0;
    return job.activeOverallSnapshotCount > 0;
};

const formatOptionStrategyCoverage = (ids: number[], hasNoOptionCoverage: boolean) => {
    const labels = [
        ...(hasNoOptionCoverage ? ['Stock/no option'] : []),
        ...Array.from(new Set(ids))
            .sort((a, b) => a - b)
            .map((id) => `OBS ${id}`)
    ];

    return labels.length > 0 ? labels.join(', ') : 'None';
};

const resolveInitialDraft = (strategy: AeroProfileStrategy): AeroDraft => {
    const activeBinding = strategy.bindings.find((binding) => binding.status === 'active') ?? null;
    const firstJob = strategy.jobs[0] ?? null;

    return {
        jobId: String(activeBinding?.sourceAeroJobId ?? firstJob?.id ?? ''),
        selectionMode: activeBinding?.selectionMode ?? 'entity_with_overall_fallback',
        requireOptionBuyingStrategyMatch: activeBinding?.requireOptionBuyingStrategyMatch ?? true
    };
};

const AeroExitSelectionSection = ({ canEdit, tradingProfileId }: { canEdit: boolean; tradingProfileId: number }) => {
    const queryClient = useQueryClient();
    const [draftsByStrategyId, setDraftsByStrategyId] = useState<Record<number, Partial<AeroDraft>>>({});

    const optionsQuery = useAppQuery({
        ...trpc.exitOptimization.fetchExitSelectionProfileOptions.queryOptions({ tradingProfileId, includeRetired: true }),
        enabled: tradingProfileId > 0 && canEdit
    });

    const refreshQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries(trpc.exitOptimization.fetchExitSelectionProfileOptions.pathFilter()),
            queryClient.invalidateQueries(trpc.exitOptimization.fetchExitSelectionBindings.pathFilter()),
            queryClient.invalidateQueries(trpc.exitOptimization.fetchExitSelectionDecisions.pathFilter())
        ]);
    };

    const createBindingMutation = useMutation({
        ...trpc.exitOptimization.createExitSelectionBinding.mutationOptions(),
        onSuccess: async (result) => {
            if (!result.success) {
                showToast(result.errorMessages.join(' ') || 'AERO exit selection binding could not be created', 'error');
                return;
            }

            showToast('AERO exit selection binding is active', 'success');
            await refreshQueries();
        },
        onError: (error) => {
            showToast(error instanceof Error ? error.message : 'AERO exit selection binding could not be created', 'error');
        }
    });

    const retireBindingMutation = useMutation({
        ...trpc.exitOptimization.retireExitSelectionBinding.mutationOptions(),
        onSuccess: async (result) => {
            if (!result.success) {
                showToast(result.errorMessages.join(' ') || 'AERO exit selection binding could not be retired', 'error');
                return;
            }

            showToast('AERO exit selection binding retired', 'success');
            await refreshQueries();
        },
        onError: (error) => {
            showToast(error instanceof Error ? error.message : 'AERO exit selection binding could not be retired', 'error');
        }
    });

    const strategies = optionsQuery.data?.strategies ?? [];

    const activeBindingCount = useMemo(
        () => strategies.reduce((count, strategy) => count + strategy.bindings.filter((binding) => binding.status === 'active').length, 0),
        [strategies]
    );

    const readDraft = (strategy: AeroProfileStrategy): AeroDraft => ({
        ...resolveInitialDraft(strategy),
        ...draftsByStrategyId[strategy.strategyId]
    });

    const updateDraft = (strategyId: number, patch: Partial<AeroDraft>) => {
        setDraftsByStrategyId((current) => ({
            ...current,
            [strategyId]: {
                ...current[strategyId],
                ...patch
            }
        }));
    };

    const createBinding = (strategy: AeroProfileStrategy) => {
        const draft = readDraft(strategy);
        const selectedJobId = Number(draft.jobId);
        const selectedJob = strategy.jobs.find((job) => job.id === selectedJobId);

        if (!selectedJob) {
            showToast('Choose a completed AERO job', 'error');
            return;
        }

        if (!isModeSupportedByJob(draft.selectionMode, selectedJob)) {
            showToast('The selected AERO job does not have snapshots for that mode', 'error');
            return;
        }

        createBindingMutation.mutate({
            sourceAeroJobId: selectedJob.id,
            strategyId: strategy.strategyId,
            tradingProfileId,
            selectionMode: draft.selectionMode,
            requireOptionBuyingStrategyMatch: draft.requireOptionBuyingStrategyMatch,
            effectiveFromTimestamp: selectedJob.earliestAvailableAtTimestamp ?? 0
        });
    };

    if (tradingProfileId <= 0) {
        return (
            <Card className="mb-4" title="AERO PIT Exit Selection">
                <div className="text-sm text-zinc-400">Save the trading profile before configuring AERO exits.</div>
            </Card>
        );
    }

    if (!canEdit) {
        return (
            <Card className="mb-4" title="AERO PIT Exit Selection">
                <div className="text-sm text-zinc-400">Only the trading profile owner can configure AERO exit selection.</div>
            </Card>
        );
    }

    return (
        <Card className="mb-4" title="AERO PIT Exit Selection">
            {optionsQuery.isLoading ? (
                <DataLoadingSpinner text="Loading AERO exit selection..." />
            ) : strategies.length === 0 ? (
                <div className="rounded-md border border-zinc-800 bg-black/20 p-3 text-sm text-zinc-400">
                    No completed AERO jobs with active point-in-time snapshots are available.
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-sm text-zinc-400">
                            {activeBindingCount > 0
                                ? `${activeBindingCount} active ${activeBindingCount === 1 ? 'binding' : 'bindings'}`
                                : 'No active AERO bindings'}
                        </div>
                        <Button size="sm" to="/user/aero" variant="outline-dark">
                            Open AERO Jobs
                        </Button>
                    </div>

                    <div className="rounded-md border border-cyan-900/60 bg-cyan-950/20 p-3 text-xs text-cyan-100">
                        Runtime selection only considers snapshot winners that are published as global exit candidates and attached to the
                        strategy. Each portfolio deployment&apos;s frozen exit selection narrows that strategy list further.
                    </div>

                    {strategies.map((strategy) => (
                        <AeroStrategyExitSelectionRow
                            canEdit={canEdit}
                            draft={readDraft(strategy)}
                            isCreating={createBindingMutation.isPending}
                            isRetiring={retireBindingMutation.isPending}
                            key={strategy.strategyId}
                            strategy={strategy}
                            onCreate={() => createBinding(strategy)}
                            onDraftChange={(patch) => updateDraft(strategy.strategyId, patch)}
                            onRetire={(bindingId) => retireBindingMutation.mutate({ bindingId })}
                        />
                    ))}
                </div>
            )}
        </Card>
    );
};

const AeroStrategyExitSelectionRow = ({
    canEdit,
    draft,
    isCreating,
    isRetiring,
    strategy,
    onCreate,
    onDraftChange,
    onRetire
}: {
    canEdit: boolean;
    draft: AeroDraft;
    isCreating: boolean;
    isRetiring: boolean;
    strategy: AeroProfileStrategy;
    onCreate: () => void;
    onDraftChange: (patch: Partial<AeroDraft>) => void;
    onRetire: (bindingId: number) => void;
}) => {
    const activeBinding = strategy.bindings.find((binding) => binding.status === 'active') ?? null;
    const selectedJob = strategy.jobs.find((job) => String(job.id) === draft.jobId) ?? null;
    const selectedModeSupported = isModeSupportedByJob(draft.selectionMode, selectedJob);

    const canCreate = canEdit && selectedJob !== null && selectedModeSupported && !isCreating;

    return (
        <div className="rounded-md border border-zinc-800 bg-black/20 p-3">
            <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <Link
                        className="text-sm font-medium text-zinc-100 hover:text-cyan-200"
                        to={`/user/tradingStrategy/${strategy.strategyId}`}
                    >
                        {strategy.strategyName ?? `Strategy ${strategy.strategyId}`}
                    </Link>
                    <div className="mt-1 text-xs text-zinc-500">Strategy ID {strategy.strategyId}</div>
                </div>
                {activeBinding ? (
                    <div className="flex flex-wrap items-center gap-2">
                        <Link className="text-xs text-cyan-300 hover:text-cyan-200" to={`/user/aero/${activeBinding.sourceAeroJobId}`}>
                            Active job #{activeBinding.sourceAeroJobId}
                        </Link>
                        <Button
                            disabled={!canEdit || isRetiring}
                            size="sm"
                            variant="outline-dark"
                            onClick={() => onRetire(activeBinding.id)}
                        >
                            Retire
                        </Button>
                    </div>
                ) : (
                    <div className="rounded bg-zinc-900 px-2 py-1 text-xs text-zinc-400">Unbound</div>
                )}
            </div>

            <div className="grid gap-3 md:grid-cols-2">
                <SearchSelect
                    noMargin
                    items={strategy.jobs.map((job) => ({
                        label: formatJobName(job),
                        name: formatJobName(job),
                        value: job.id
                    }))}
                    label="AERO job"
                    mode="single"
                    placeholder="Select an AERO job"
                    value={draft.jobId ? Number(draft.jobId) : null}
                    onChange={(value) => onDraftChange({ jobId: value !== null ? String(value) : '' })}
                />
                <FormSelect
                    noMargin
                    label="Selection mode"
                    options={SELECTION_MODE_OPTIONS}
                    value={draft.selectionMode}
                    onChange={(event) => onDraftChange({ selectionMode: event.target.value as AeroExitSelectionMode })}
                />
            </div>

            <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                <div>
                    <FormCheckbox
                        checked={draft.requireOptionBuyingStrategyMatch}
                        label="Match each trade's option strategy"
                        labelClassName="text-zinc-200"
                        onChange={(event) => onDraftChange({ requireOptionBuyingStrategyMatch: event.target.checked })}
                    />
                    {selectedJob && (
                        <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-zinc-400 md:grid-cols-4">
                            <div>
                                <div className="text-zinc-500">Overall snapshots</div>
                                <div className="font-medium text-zinc-200">{selectedJob.activeOverallSnapshotCount}</div>
                            </div>
                            <div>
                                <div className="text-zinc-500">Entity snapshots</div>
                                <div className="font-medium text-zinc-200">{selectedJob.activeEntitySnapshotCount}</div>
                            </div>
                            <div>
                                <div className="text-zinc-500">Facts</div>
                                <div className="font-medium text-zinc-200">{selectedJob.maxEligibleFacts.toLocaleString()}</div>
                            </div>
                            <div>
                                <div className="text-zinc-500">Effective from</div>
                                <div className="font-medium text-zinc-200">
                                    {formatUnixTimestamp(selectedJob.earliestAvailableAtTimestamp)}
                                </div>
                            </div>
                            <div className="col-span-2 md:col-span-2">
                                <div className="text-zinc-500">Overall option coverage</div>
                                <div className="break-words font-medium text-zinc-200">
                                    {formatOptionStrategyCoverage(
                                        selectedJob.coveredOverallOptionBuyingStrategyIds,
                                        selectedJob.hasNoOptionBuyingStrategyOverallCoverage
                                    )}
                                </div>
                            </div>
                            <div className="col-span-2 md:col-span-2">
                                <div className="text-zinc-500">Entity option coverage</div>
                                <div className="break-words font-medium text-zinc-200">
                                    {formatOptionStrategyCoverage(
                                        selectedJob.coveredEntityOptionBuyingStrategyIds,
                                        selectedJob.hasNoOptionBuyingStrategyEntityCoverage
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {!selectedModeSupported && (
                        <div className="mt-2 text-xs text-amber-200">This job does not have snapshots for that selection mode.</div>
                    )}
                </div>
                <Button disabled={!canCreate} isLoading={isCreating} size="sm" onClick={onCreate}>
                    {activeBinding ? 'Replace Binding' : 'Activate'}
                </Button>
            </div>
        </div>
    );
};

export default AeroExitSelectionSection;
